import React, { useState, useEffect } from "react";
import { Shield, Loader, RefreshCw, AlertTriangle, Trash2, Eye, EyeOff, Server } from "lucide-react";
import Navigation from "../components/Navigation";
import { useApi } from "../hooks/useApi";
import { useRBAC } from "../hooks/useRBAC";

const FortinetConfig = () => {
  const apiCall = useApi();
  const { canAdministerOrganization } = useRBAC();
  const org = JSON.parse(localStorage.getItem("selectedOrganization") || "{}");
  const isAdmin = canAdministerOrganization(org?.organizationId);

  const [config, setConfig]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [saved, setSaved]     = useState(false);

  const [fgUrl, setFgUrl]           = useState("");
  const [fgKey, setFgKey]           = useState("");
  const [showFgKey, setShowFgKey]   = useState(false);
  const [fmgUrl, setFmgUrl]         = useState("");
  const [fmgKey, setFmgKey]         = useState("");
  const [showFmgKey, setShowFmgKey] = useState(false);
  const [adom, setAdom]             = useState("root");
  const [frequency, setFrequency]   = useState(24);
  const [isEnabled, setIsEnabled]   = useState(true);
  const [saving, setSaving]         = useState(false);
  const [deleting, setDeleting]     = useState(false);

  const loadConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiCall("GET", "/fortinet/config");
      setConfig(data);
      if (data) {
        setFgUrl(data.fortigateUrl || "");
        setFmgUrl(data.fortimanagerUrl || "");
        setAdom(data.fortimanagerAdom || "root");
        setFrequency(data.scanFrequencyHours || 24);
        setIsEnabled(data.isEnabled ?? true);
      }
    } catch {
      setError("Failed to load configuration.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadConfig(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const payload = {
        fortigateUrl: fgUrl.trim() || null,
        fortigateApiKey: fgKey.trim() || null,
        fortimanagerUrl: fmgUrl.trim() || null,
        fortimanagerApiKey: fmgKey.trim() || null,
        fortimanagerAdom: adom.trim() || "root",
        isEnabled,
        scanFrequencyHours: Number(frequency) || 24,
      };
      const saved = await apiCall("POST", "/fortinet/config", payload);
      setConfig(saved);
      setFgKey("");
      setFmgKey("");
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError("Failed to save configuration.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Remove all Fortinet configuration? This will stop automated scanning.")) return;
    setDeleting(true);
    setError(null);
    try {
      await apiCall("DELETE", "/fortinet/config");
      setConfig(null);
      setFgUrl(""); setFgKey(""); setFmgUrl(""); setFmgKey("");
      setAdom("root"); setFrequency(24); setIsEnabled(true);
    } catch {
      setError("Failed to delete configuration.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-theme-primary">
      <Navigation />
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">

        <div className="mb-6">
          <h1 className="text-3xl font-bold text-content-primary">
            <span className="text-orange-600">Fortinet Connector Config</span>
          </h1>
          <p className="mt-2 text-gray-600">
            Connect FortiGate and FortiManager to pull security signals into GRC risks
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader className="h-8 w-8 animate-spin text-orange-600" />
          </div>
        ) : (
          <>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-red-700 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" /> {error}
                </p>
              </div>
            )}
            {saved && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-green-700">Configuration saved successfully.</p>
              </div>
            )}

            {/* FortiGate Section */}
            <div className="bg-theme-secondary rounded-lg shadow-theme border border-border p-6 mb-6">
              <h2 className="text-lg font-semibold text-content-primary mb-4 flex items-center gap-2">
                <Server className="h-5 w-5 text-orange-600" />
                FortiGate
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                <div>
                  <label className="block text-sm font-medium text-content-secondary mb-1">
                    FortiGate URL
                    <span className="ml-1 font-normal text-xs text-content-secondary">(e.g. https://192.168.1.1)</span>
                  </label>
                  <input
                    type="url"
                    value={fgUrl}
                    onChange={e => setFgUrl(e.target.value)}
                    placeholder="https://192.168.1.1"
                    disabled={!isAdmin}
                    className="w-full rounded-md border border-border bg-theme-primary text-content-primary shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-content-secondary mb-1">
                    API Key
                    {config?.fortigateApiKeySet && (
                      <span className="ml-1 font-normal text-xs text-green-600">· saved</span>
                    )}
                  </label>
                  <div className="relative">
                    <input
                      type={showFgKey ? "text" : "password"}
                      value={fgKey}
                      onChange={e => setFgKey(e.target.value)}
                      placeholder={config?.fortigateApiKeySet ? "••••••••••••••• (enter new to replace)" : "FortiGate API key"}
                      disabled={!isAdmin}
                      className="w-full rounded-md border border-border bg-theme-primary text-content-primary shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm px-3 py-2 pr-10"
                    />
                    <button type="button" onClick={() => setShowFgKey(p => !p)} className="absolute right-3 top-2.5 text-content-secondary">
                      {showFgKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-content-secondary">
                    Create under System → Administrators → REST API Admin in FortiOS.
                  </p>
                </div>
              </div>
            </div>

            {/* FortiManager Section */}
            <div className="bg-theme-secondary rounded-lg shadow-theme border border-border p-6 mb-6">
              <h2 className="text-lg font-semibold text-content-primary mb-1 flex items-center gap-2">
                <Shield className="h-5 w-5 text-orange-600" />
                FortiManager
                <span className="text-sm font-normal text-content-secondary ml-1">(optional)</span>
              </h2>
              <p className="text-sm text-content-secondary mb-4">
                Required for FortiAP, FortiSwitch, and multi-device policy coverage.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                <div>
                  <label className="block text-sm font-medium text-content-secondary mb-1">FortiManager URL</label>
                  <input
                    type="url"
                    value={fmgUrl}
                    onChange={e => setFmgUrl(e.target.value)}
                    placeholder="https://fortimanager.example.com"
                    disabled={!isAdmin}
                    className="w-full rounded-md border border-border bg-theme-primary text-content-primary shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-content-secondary mb-1">
                    API Key
                    {config?.fortimanagerApiKeySet && (
                      <span className="ml-1 font-normal text-xs text-green-600">· saved</span>
                    )}
                  </label>
                  <div className="relative">
                    <input
                      type={showFmgKey ? "text" : "password"}
                      value={fmgKey}
                      onChange={e => setFmgKey(e.target.value)}
                      placeholder={config?.fortimanagerApiKeySet ? "••••••••••••••• (enter new to replace)" : "FortiManager API key"}
                      disabled={!isAdmin}
                      className="w-full rounded-md border border-border bg-theme-primary text-content-primary shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm px-3 py-2 pr-10"
                    />
                    <button type="button" onClick={() => setShowFmgKey(p => !p)} className="absolute right-3 top-2.5 text-content-secondary">
                      {showFmgKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-content-secondary mb-1">
                    ADOM
                    <span className="ml-1 font-normal text-xs text-content-secondary">(administrative domain, default: root)</span>
                  </label>
                  <input
                    type="text"
                    value={adom}
                    onChange={e => setAdom(e.target.value)}
                    placeholder="root"
                    disabled={!isAdmin}
                    className="w-full rounded-md border border-border bg-theme-primary text-content-primary shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm px-3 py-2"
                  />
                </div>
              </div>
            </div>

            {/* Scan Settings */}
            <div className="bg-theme-secondary rounded-lg shadow-theme border border-border p-6 mb-6">
              <h2 className="text-lg font-semibold text-content-primary mb-4 flex items-center gap-2">
                <RefreshCw className="h-5 w-5 text-orange-600" />
                Scan Settings
              </h2>

              <div className="flex flex-wrap items-center gap-6 mb-5">
                <div>
                  <label className="block text-sm font-medium text-content-secondary mb-1">Scan Frequency</label>
                  <select
                    value={frequency}
                    onChange={e => setFrequency(Number(e.target.value))}
                    disabled={!isAdmin}
                    className="rounded-md border border-border bg-theme-primary text-content-primary shadow-sm focus:border-orange-500 sm:text-sm px-3 py-2"
                  >
                    <option value={6}>Every 6 hours</option>
                    <option value={12}>Every 12 hours</option>
                    <option value={24}>Every 24 hours</option>
                    <option value={48}>Every 48 hours</option>
                    <option value={168}>Weekly</option>
                  </select>
                </div>
                <div className="flex items-center gap-2 mt-5">
                  <input
                    type="checkbox"
                    id="fg-enabled"
                    checked={isEnabled}
                    onChange={e => setIsEnabled(e.target.checked)}
                    disabled={!isAdmin}
                    className="rounded border-gray-300 text-orange-600"
                  />
                  <label htmlFor="fg-enabled" className="text-sm text-content-primary cursor-pointer">
                    Enable automatic scanning
                  </label>
                </div>
              </div>

              {isAdmin && (
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-600 hover:bg-orange-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <><Loader className="h-4 w-4 animate-spin" />Saving...</>
                    ) : (
                      <><RefreshCw className="h-4 w-4" />Save Settings</>
                    )}
                  </button>

                  {config && (
                    <button
                      onClick={handleDelete}
                      disabled={deleting}
                      className="inline-flex items-center gap-2 px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 hover:bg-red-50 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {deleting ? (
                        <><Loader className="h-4 w-4 animate-spin" />Removing...</>
                      ) : (
                        <><Trash2 className="h-4 w-4" />Remove Config</>
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FortinetConfig;
